import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import BuildIcon from "@mui/icons-material/Build";
import { format, parseISO } from "date-fns";
import {
  InvestmentApplication,
  INVESTMENT_APPLICATION_STATUS_LABELS,
} from "../../../entities/admin-investments";
import {
  getCaliberColor,
  renderCaliberShortName,
} from "../../../shared/lib/caliber";
import { ResidentCaliber } from "../../../entities/admin-events";

interface InvestmentsApplicationViewDialogProps {
  open: boolean;
  application: InvestmentApplication | null;
  isLoading: boolean;
  isModerating: boolean;
  onClose: () => void;
  onApprove?: () => void;
  onNeedsRevision?: () => void;
  onReject?: () => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd.MM.yyyy HH:mm");
  } catch {
    return value;
  }
};

const InfoRow = ({ label, value }: { label: string; value?: string | null }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value?.trim() || "—"}</Typography>
  </Box>
);

export const InvestmentsApplicationViewDialog = ({
  open,
  application,
  isLoading,
  isModerating,
  onClose,
  onApprove,
  onNeedsRevision,
  onReject,
}: InvestmentsApplicationViewDialogProps) => {
  if (!application && !isLoading) return null;

  const canModerate = application?.status === "pendingReview";
  const author = application?.author;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Заявка на инвестиции</DialogTitle>
      <DialogContent>
        {isLoading || !application ? (
          <Typography color="text.secondary">Загрузка...</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={INVESTMENT_APPLICATION_STATUS_LABELS[application.status]}
                color={
                  application.status === "pendingReview"
                    ? "warning"
                    : application.status === "approved"
                      ? "success"
                      : application.status === "rejected"
                        ? "error"
                        : "default"
                }
              />
              {application.targetInvestmentId ? (
                <Chip
                  size="small"
                  label={`Редактирование #${application.targetInvestmentId}`}
                  color="info"
                />
              ) : (
                <Chip size="small" label="Новая заявка" />
              )}
            </Stack>

            {author && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar src={author.avatarUrl} alt={author.firstName}>
                  {author.firstName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2">
                    {author.firstName} {author.lastName}
                  </Typography>
                  <Chip
                    size="small"
                    sx={{ mt: 0.5 }}
                    color={getCaliberColor(author.caliber as ResidentCaliber)}
                    label={renderCaliberShortName(author.caliber as ResidentCaliber)}
                  />
                </Box>
              </Stack>
            )}

            {application.logoUrl && (
              <Box
                component="img"
                src={application.logoUrl}
                alt={application.title}
                sx={{
                  width: 96,
                  height: 96,
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            )}

            <Typography variant="h6">{application.title || "Без названия"}</Typography>
            <Typography variant="body2" whiteSpace="pre-wrap">
              {application.description || "—"}
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <InfoRow label="ФИО" value={application.fullName} />
              <InfoRow label="Email" value={application.email} />
              <InfoRow label="Телефон" value={application.phone} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <InfoRow label="Telegram" value={application.telegram} />
              <InfoRow label="WhatsApp" value={application.whatsapp} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Сайт
                </Typography>
                {application.website ? (
                  <Link href={application.website} target="_blank" rel="noopener" display="block">
                    {application.website}
                  </Link>
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Video Pitch Deck
              </Typography>
              {application.videoPitchUrl ? (
                <Link
                  href={application.videoPitchUrl}
                  target="_blank"
                  rel="noopener"
                  display="block"
                >
                  {application.videoPitchUrl}
                </Link>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Pitch Deck
              </Typography>
              {application.pitchDeckUrl ? (
                <Link
                  href={application.pitchDeckUrl}
                  target="_blank"
                  rel="noopener"
                  display="block"
                >
                  Открыть PDF
                </Link>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Box>

            {application.rejectionReason && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "warning.light",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Причина отклонения / доработки
                </Typography>
                <Typography variant="body2">{application.rejectionReason}</Typography>
              </Box>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <InfoRow label="Создана" value={formatDate(application.createdAt)} />
              <InfoRow label="Обновлена" value={formatDate(application.updatedAt)} />
              <InfoRow label="Проверена" value={formatDate(application.reviewedAt)} />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {canModerate && (
          <>
            <Button
              color="success"
              startIcon={<ThumbUpIcon />}
              onClick={onApprove}
              disabled={isModerating}
            >
              Одобрить
            </Button>
            <Button
              color="warning"
              startIcon={<BuildIcon />}
              onClick={onNeedsRevision}
              disabled={isModerating}
            >
              На доработку
            </Button>
            <Button
              color="error"
              startIcon={<ThumbDownIcon />}
              onClick={onReject}
              disabled={isModerating}
            >
              Отклонить
            </Button>
          </>
        )}
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};
